import { useState, useEffect, useContext } from 'react';
import { Box, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttDialog, SttForm } from '../../../sporttia/all';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    line: any;
    onClose: () => void;
    onUpdate?: (line: any) => void;
};

export default function FileCSVLineDialog({
    line,
    onClose,
    onUpdate,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [internalLine, setInternalLine] = useState<any>();

    /**
     * Input line changes
     */
    useEffect(() => {
        setInternalLine(line);
    }, [line]);

    /**
     * Save line
     */
    const saveLine = () => {
        const newValues = [];
        for (let i = 0; i < (cxt?.constants?.numFieldsToUserImport || 0); i++) {
            newValues.push(internalLine[`field_${i}`]);
        }

        cxt?.api('PUT', `/files/csvs/lines/${internalLine.id}`, {
            params: {
                values: newValues,
            },
            success: (r: any) => {
                if (onUpdate) onUpdate(r.fileCSVLine);
            },
        });
    };

    /**
     * Delete line
     */
    const delLine = () => {
        cxt?.api('DELETE', `/files/csvs/lines/${internalLine.id}`, {
            confirmation: true,
            success: (r: any) => {
                if (onUpdate) onUpdate(r.fileCSVLine);
            },
        });
    };

    /**
     * Recover line
     */
    const recoverLine = () => {
        cxt?.api('PUT', `/files/csvs/lines/${internalLine.id}/recover`, {
            success: (r: any) => {
                if (onUpdate) onUpdate(r.fileCSVLine);
            },
        });
    };

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setInternalLine({ ...internalLine, [name]: value });
    };

    // -----| Render |-----
    return (
        <>
            {internalLine && (
                <SttDialog
                    title={t('Line')}
                    open={internalLine !== null}
                    onClose={onClose}
                    content={
                        <Box>
                            {line && line.error && (
                                <Box mb={3}>
                                    <Alert severity="error" variant="filled">
                                        {line.error}
                                    </Alert>
                                </Box>
                            )}

                            <SttForm
                                elements={[
                                    ...Array(
                                        cxt?.constants?.numFieldsToUserImport ||
                                            0,
                                    ).keys(),
                                ].map((num: any) => ({
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    type: 'text',
                                    name: `field_${num}`,
                                    caption: `#${num}`,
                                    value: internalLine[`field_${num}`],
                                }))}
                                onChangeForm={handleForm}
                            />
                        </Box>
                    }
                    buttons={[
                        {
                            show: internalLine && internalLine.trash,
                            type: 'recover',
                            onClick: () => recoverLine(),
                            permission: PERMISSIONS.CUSTOMER.CREATE,
                        },
                        {
                            show: internalLine && !internalLine.trash,
                            type: 'delete',
                            onClick: () => delLine(),
                            permission: PERMISSIONS.CUSTOMER.CREATE,
                        },
                        {
                            show: internalLine && !internalLine.trash,
                            type: 'save',
                            onClick: () => saveLine(),
                            permission: PERMISSIONS.CUSTOMER.CREATE,
                        },
                        {
                            type: 'close',
                            onClick: onClose,
                        },
                    ]}
                />
            )}
        </>
    );
}
